import { LocaleCode, RiskLevel, SemanticEvaluation, TerminologyConcept } from './types';

export interface TranslationRequest {
  sourceText: string;
  locale: LocaleCode;
  context: string;
  riskLevel: RiskLevel;
  terminology: TerminologyConcept[];
}

export interface TranslationResponse {
  text: string;
  provider: string;
  model: string;
  promptVersion: string;
}

export interface AssistantRequest {
  locale: LocaleCode;
  question: string;
  terminology: TerminologyConcept[];
}

export interface AIProvider {
  name: string;
  translate(request: TranslationRequest): Promise<TranslationResponse>;
  classifyRisk(sourceText: string, context: string): Promise<RiskLevel>;
  evaluateTranslation(sourceText: string, localizedText: string, locale: LocaleCode): Promise<SemanticEvaluation>;
  detectTerminology(sourceText: string, concepts: TerminologyConcept[]): Promise<TerminologyConcept[]>;
  generateAssistantResponse(request: AssistantRequest): Promise<TranslationResponse>;
}

const dictionaries: Record<LocaleCode, Record<string, string>> = {
  en: {},
  ja: {
    'Exposure Management': 'エクスポージャー管理',
    'Start scan': 'スキャンを開始',
    'Delete scan': 'スキャンを削除',
    'Critical vulnerabilities': '重大な脆弱性',
    'Asset Risk Score': 'アセットリスクスコア',
    'Review remediation guidance before applying changes.': '変更を適用する前に修復ガイダンスを確認してください。',
    'A remote code execution vulnerability affects the service.': 'リモートコード実行の脆弱性がサービスに影響します。',
    'Upgrade to version 4.2.1 and restart the affected service.': 'バージョン4.2.1にアップグレードし、影響を受けるサービスを再起動してください。',
    'See and reduce cyber risk': 'サイバーリスクを可視化して低減',
    'Prioritize the exposures that matter most.': '最も重要なエクスポージャーを優先します。',
    'Cyber Exposure Management': 'サイバーエクスポージャー管理',
  },
  fr: {
    'Exposure Management': 'Gestion de l’exposition',
    'Start scan': 'Démarrer l’analyse',
    'Delete scan': 'Supprimer l’analyse',
    'Critical vulnerabilities': 'Vulnérabilités critiques',
    'Asset Risk Score': 'Score de risque de l’actif',
    'Review remediation guidance before applying changes.': 'Consultez les recommandations de remédiation avant d’appliquer les modifications.',
    'A remote code execution vulnerability affects the service.': 'Une vulnérabilité d’exécution de code à distance affecte le service.',
    'Upgrade to version 4.2.1 and restart the affected service.': 'Passez à la version 4.2.1 et redémarrez le service concerné.',
    'See and reduce cyber risk': 'Visualiser et réduire le risque cyber',
    'Prioritize the exposures that matter most.': 'Priorisez les expositions les plus importantes.',
    'Cyber Exposure Management': 'Gestion de l’exposition cyber',
  },
  de: {
    'Exposure Management': 'Exposure Management',
    'Start scan': 'Scan starten',
    'Delete scan': 'Scan löschen',
    'Critical vulnerabilities': 'Kritische Schwachstellen',
    'Asset Risk Score': 'Asset-Risiko-Score',
    'Review remediation guidance before applying changes.': 'Prüfen Sie die Behebungshinweise, bevor Sie Änderungen anwenden.',
    'A remote code execution vulnerability affects the service.': 'Eine Schwachstelle für Remote Code Execution betrifft den Dienst.',
    'Upgrade to version 4.2.1 and restart the affected service.': 'Aktualisieren Sie auf Version 4.2.1 und starten Sie den betroffenen Dienst neu.',
    'See and reduce cyber risk': 'Cyberrisiken erkennen und reduzieren',
    'Prioritize the exposures that matter most.': 'Priorisieren Sie die wichtigsten Exposures.',
    'Cyber Exposure Management': 'Cyber Exposure Management',
  },
  es: {
    'Exposure Management': 'Gestión de exposición',
    'Start scan': 'Iniciar análisis',
    'Delete scan': 'Eliminar análisis',
    'Critical vulnerabilities': 'Vulnerabilidades críticas',
    'Asset Risk Score': 'Puntuación de riesgo del activo',
    'Review remediation guidance before applying changes.': 'Revise la guía de remediación antes de aplicar cambios.',
    'A remote code execution vulnerability affects the service.': 'Una vulnerabilidad de ejecución remota de código afecta al servicio.',
    'Upgrade to version 4.2.1 and restart the affected service.': 'Actualice a la versión 4.2.1 y reinicie el servicio afectado.',
    'See and reduce cyber risk': 'Vea y reduzca el riesgo cibernético',
    'Prioritize the exposures that matter most.': 'Priorice las exposiciones más importantes.',
    'Cyber Exposure Management': 'Gestión de exposición cibernética',
  },
  'pt-BR': {
    'Exposure Management': 'Gerenciamento de exposição',
    'Start scan': 'Iniciar verificação',
    'Delete scan': 'Excluir verificação',
    'Critical vulnerabilities': 'Vulnerabilidades críticas',
    'Asset Risk Score': 'Pontuação de risco do ativo',
    'Review remediation guidance before applying changes.': 'Revise a orientação de remediação antes de aplicar alterações.',
    'A remote code execution vulnerability affects the service.': 'Uma vulnerabilidade de execução remota de código afeta o serviço.',
    'Upgrade to version 4.2.1 and restart the affected service.': 'Atualize para a versão 4.2.1 e reinicie o serviço afetado.',
    'See and reduce cyber risk': 'Veja e reduza o risco cibernético',
    'Prioritize the exposures that matter most.': 'Priorize as exposições mais importantes.',
    'Cyber Exposure Management': 'Gerenciamento de exposição cibernética',
  },
  ar: {
    'Exposure Management': 'إدارة التعرض',
    'Start scan': 'بدء الفحص',
    'Delete scan': 'حذف الفحص',
    'Critical vulnerabilities': 'الثغرات الحرجة',
    'Asset Risk Score': 'درجة مخاطر الأصل',
    'Review remediation guidance before applying changes.': 'راجع إرشادات المعالجة قبل تطبيق التغييرات.',
    'A remote code execution vulnerability affects the service.': 'تؤثر ثغرة تنفيذ تعليمات برمجية عن بُعد على الخدمة.',
    'Upgrade to version 4.2.1 and restart the affected service.': 'قم بالترقية إلى الإصدار 4.2.1 ثم أعد تشغيل الخدمة المتأثرة.',
    'See and reduce cyber risk': 'رؤية مخاطر الأمن السيبراني وتقليلها',
    'Prioritize the exposures that matter most.': 'رتّب أولويات حالات التعرض الأكثر أهمية.',
    'Cyber Exposure Management': 'إدارة التعرض السيبراني',
  },
};

function fallbackTranslate(sourceText: string, locale: LocaleCode): string {
  const prefix = dictionaries[locale]?.['Exposure Management'] ?? locale;
  return `${prefix}: ${sourceText}`;
}

export class MockAIProvider implements AIProvider {
  name = 'mock';

  async translate(request: TranslationRequest): Promise<TranslationResponse> {
    let text = dictionaries[request.locale][request.sourceText] ?? fallbackTranslate(request.sourceText, request.locale);
    if (request.sourceText.includes('Review remediation') && request.locale === 'de') {
      text = 'Prüfen Sie die bad-Remediation-de Hinweise, bevor Sie Änderungen anwenden.';
    }
    return {
      text,
      provider: 'mock',
      model: request.riskLevel === 'HIGH' ? 'mock-secure-localizer-v2' : 'mock-fast-localizer-v1',
      promptVersion: request.riskLevel === 'HIGH' ? 'loc-high-risk-2026-08' : 'loc-standard-2026-08',
    };
  }

  async classifyRisk(sourceText: string, context: string): Promise<RiskLevel> {
    const value = `${sourceText} ${context}`.toLowerCase();
    if (value.includes('delete') || value.includes('remediation') || value.includes('warning')) return 'HIGH';
    if (value.includes('guidance') || value.includes('critical') || value.includes('score')) return 'MEDIUM';
    return 'LOW';
  }

  async evaluateTranslation(sourceText: string, localizedText: string, locale: LocaleCode): Promise<SemanticEvaluation> {
    const hasForbidden = localizedText.includes('bad-');
    const criticalOmission = /CVE-\d{4}-\d+/.test(sourceText) && !/CVE-\d{4}-\d+/.test(localizedText);
    const securityAmbiguity = hasForbidden || localizedText.toLowerCase().includes('maybe');
    const base = hasForbidden ? 2 : 4;
    const fluency = locale === 'ar' ? 3.6 : 3.8;
    const score = ((base + base + fluency + 3.8) / 16);
    return {
      accuracy: base,
      terminologyCompliance: hasForbidden ? 1 : 4,
      fluency,
      intentPreservation: criticalOmission ? 1 : 3.8,
      securityAmbiguity,
      criticalOmission,
      explanation: hasForbidden
        ? 'A forbidden terminology variant was detected in the candidate.'
        : 'Meaning, tone, and domain intent are preserved for the demo rubric.',
      overallScore: Number(score.toFixed(2)),
    };
  }

  async detectTerminology(sourceText: string, concepts: TerminologyConcept[]): Promise<TerminologyConcept[]> {
    const lower = sourceText.toLowerCase();
    return concepts.filter((concept) => lower.includes(concept.canonicalEnglish.toLowerCase()));
  }

  async generateAssistantResponse(request: AssistantRequest): Promise<TranslationResponse> {
    const common = 'CVE-2026-12345 4.2.1 prod-db-07 /opt/example/bin/service --version';
    const responses: Record<LocaleCode, string> = {
      en: `Prioritize the critical vulnerability ${common}. Validate exposure, schedule remediation, upgrade safely, and preserve the evidence command as data.`,
      ja: `重大な脆弱性 ${common} を優先してください。影響範囲を確認し、修復を計画し、安全にアップグレードしてください。`,
      fr: `Priorisez la vulnérabilité critique ${common}. Vérifiez l’exposition, planifiez la remédiation et conservez les preuves intactes.`,
      de: `Priorisieren Sie die kritische Schwachstelle ${common}. Prüfen Sie die Exposure, planen Sie die Behebung und bewahren Sie Nachweise unverändert auf.`,
      es: `Priorice la vulnerabilidad crítica ${common}. Valide la exposición, planifique la remediación y conserve la evidencia sin cambios.`,
      'pt-BR': `Priorize a vulnerabilidade crítica ${common}. Valide a exposição, planeje a remediação e preserve as evidências sem alterações.`,
      ar: `أعطِ الأولوية للثغرة الحرجة ${common}. تحقّق من التعرض، وخطّط للمعالجة، وحافظ على الأدلة كما هي.`,
    };
    return {
      text: responses[request.locale],
      provider: 'mock',
      model: 'mock-locale-native-assistant-v1',
      promptVersion: 'assistant-locale-native-2026-08',
    };
  }
}

export class OpenAIProvider extends MockAIProvider {
  name = 'openai';
}

export function getAIProvider(): AIProvider {
  const configured = process.env.REACT_APP_AI_PROVIDER ?? process.env.AI_PROVIDER ?? 'mock';
  return configured === 'openai' ? new OpenAIProvider() : new MockAIProvider();
}
